import { useQueryClient } from '@tanstack/react-query';
import { Input } from 'antd';
import { type ChangeEvent, useEffect, useState } from 'react';
import { updateFile } from '../../fetchHooks/updateFile';

export type FileUpdateDebouncedProps = {
  label: string;
  propertyName: string;
  fileId: number;
  value: string | number;
  style?: React.CSSProperties;
  isNumber?: boolean;
};

export const FileUpdateDebounced: React.FC<FileUpdateDebouncedProps> = ({
  label,
  propertyName,
  fileId,
  value,
  style,
  isNumber = false,
}) => {
  const [inputValue, setInputValue] = useState<string | number>(value);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  const isValueUpdated = inputValue === value;

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;

    if (isNumber) {
      const numericValue = Number(newValue);
      if (!Number.isNaN(numericValue)) {
        setInputValue(newValue);
      }
    } else {
      setInputValue(newValue);
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newDebounceTimer = setTimeout(() => {
      updateFile({ [propertyName]: newValue, id: fileId }, () => {
        void queryClient.invalidateQueries({ queryKey: ['useImages'] });
        void queryClient.invalidateQueries({ queryKey: ['useAllFiles'] });
        void queryClient.invalidateQueries({ queryKey: ['useFilesOnly'] });
      });
    }, 2000);

    setDebounceTimer(newDebounceTimer);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <Input
      placeholder={label}
      value={inputValue}
      addonBefore={`${label}:`}
      status={!isValueUpdated ? 'warning' : undefined}
      onChange={handleInputChange}
      style={style}
    />
  );
};

export default FileUpdateDebounced;
